import {DataStatus} from 'platform/components';

import {useRef, useState} from 'react';

import {isNotNil, omit} from 'ramda';

import {businessCaseRoutes, testIds} from '@dms/routes';
import {queryParams, useLazyDataQuery} from '@dms/shared';

import {composePath, useNavigate, useOnMount} from 'shared/web';

import {FINANCING_ENDING_END_DATE_PERIOD} from '../../constants/FINANCING_ENDING_END_DATE_PERIOD';
import {FINANCING_ENDING_GRID_CODE} from '../../constants/FINANCING_ENDING_GRID_CODE';

export type FinancingEndingEndDateQueryProps = {
  endDate: string;
};

// Opens the grid filtered to exactly one end date (a `{from: d, to: d}` range, never a wider one).
// A failed data query shows an error instead of the grid: the default view would list other dates.
export function FinancingEndingEndDateQuery(props: FinancingEndingEndDateQueryProps) {
  const navigate = useNavigate();
  const [getQueryParams] = useLazyDataQuery(FINANCING_ENDING_GRID_CODE);
  const [hasFailed, setHasFailed] = useState(false);
  // A late resolve after the user left must not redirect the page they went to.
  const isMountedRef = useRef(false);

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
    }
  );

  useOnMount(() => {
    getQueryParams({filters: {endOfFinancing: {from: props.endDate, to: props.endDate}}}).then(
      (dataGridParams) => {
        if (!isMountedRef.current) {
          return;
        }

        if (isNotNil(dataGridParams)) {
          navigate(
            composePath(businessCaseRoutes.financingEnding, {
              // `useLazyDataQuery` merges the current URL params; drop `endDate`, or this branch
              // renders again and the spinner never ends. The period keeps the tabs off the exact date.
              queryParams: {
                ...omit([queryParams.BUSINESS_CASE_FINANCING_ENDING_END_DATE], dataGridParams),
                [queryParams.BUSINESS_CASE_FINANCING_ENDING_PERIOD]:
                  FINANCING_ENDING_END_DATE_PERIOD,
              },
              isSearchParamsPreserved: false,
            }),
            {replace: true}
          );
        } else {
          setHasFailed(true);
        }
      }
    );
  });

  return (
    <DataStatus
      isLoading={!hasFailed}
      isError={hasFailed}
      data-testid={testIds.businessCase.financingEnding('endDateQuery')}
    />
  );
}
